<template>
  <div
    ref="panelRef"
    class="main-menu-panel"
    :style="panelStyle"
    @mouseenter="$emit('mouse-enter-panel')"
    @mouseleave="$emit('mouse-leave-panel')"
  >
    <ul class="main-menu-panel__list">
      <component
        :is="resolveComponent(child)"
        v-for="child in visibleItems"
        :key="child.header || child.title"
        :item="child"
        :level="level"
        @close-all="$emit('close-all')"
        @navigate="$emit('navigate')"
        @mouse-enter-panel="$emit('mouse-enter-panel')"
        @mouse-leave-panel="$emit('mouse-leave-panel')"
      />
    </ul>
  </div>
</template>

<script>
import {
  ref,
  computed,
  provide,
  onMounted,
  onBeforeUnmount,
  nextTick,
} from '@vue/composition-api'
import MainMenuItem from './MainMenuItem.vue'

export default {
  name: 'MainMenuPanel',
  components: {
    MainMenuItem,
  },
  beforeCreate() {
    // Lazy registration to resolve circular dependency: MainMenuGroup <-> MainMenuPanel
    this.$options.components.MainMenuGroup = require('./MainMenuGroup.vue').default
  },
  props: {
    items: {
      type: Array,
      required: true,
    },
    anchorEl: {
      type: null, // HTMLElement
      default: null,
    },
    level: {
      type: Number,
      default: 0,
    },
  },
  setup(props) {
    const panelRef = ref(null)
    const panelStyle = ref({})

    // Provide a reactive id so sibling MainMenuGroup children can coordinate:
    // when one group opens it sets activeGroupId to its own uid; siblings
    // watching this ref close themselves immediately, preventing overlap.
    const activeGroupId = ref(null)
    provide('panelActiveGroupId', activeGroupId)

    const resolveComponent = (item) => {
      return item.children ? 'main-menu-group' : 'main-menu-item'
    }

    // Filter items that exist (visibleItems is just a passthrough — ACL filtering is done in child components)
    const visibleItems = computed(() => props.items || [])

    const recalcPosition = () => {
      if (!props.anchorEl) return

      nextTick(() => {
        const anchor = props.anchorEl
        const rect = anchor.getBoundingClientRect()
        const panel = panelRef.value
        if (!panel) return

        const panelWidth = panel.offsetWidth
        // Use scrollHeight so we always get the natural content height,
        // even when maxHeight is already constraining the panel.
        const panelHeight = panel.scrollHeight
        const viewportW = window.innerWidth
        const viewportH = window.innerHeight
        const gap = 2

        let top, left

        if (props.level === 0) {
          // Level 0: open below the anchor
          top = rect.bottom + gap
          left = rect.left
        } else {
          // Level >= 1: open to the right of the anchor
          top = rect.top
          left = rect.right + gap

          // If doesn't fit on the right, open on the left
          if (left + panelWidth > viewportW) {
            left = rect.left - panelWidth - gap
          }
        }

        // Clamp vertical position so panel doesn't overflow bottom
        const maxTop = viewportH - panelHeight - 8
        if (top > maxTop) {
          top = Math.max(8, maxTop)
        }

        // If panel height is too tall for viewport, add scroll
        const availableHeight = viewportH - top - 8
        const style = {
          position: 'fixed',
          top: `${top - 6}px`,
          left: `${left - 2}px`,
          zIndex: 11000 + props.level,
        }

        if (panelHeight > availableHeight) {
          style.maxHeight = `${availableHeight}px`
          style.overflowY = 'auto'
          style.overflowX = 'hidden'
        }

        panelStyle.value = style
      })
    }

    // Ignore scroll events that originate from inside the panel itself
    // (those are the user scrolling the menu list, not the page moving).
    const onScroll = (e) => {
      const panel = panelRef.value
      if (panel && panel.contains(e.target)) return
      recalcPosition()
    }

    onMounted(() => {
      recalcPosition()
      window.addEventListener('resize', recalcPosition)
      window.addEventListener('scroll', onScroll, true)
    })

    onBeforeUnmount(() => {
      window.removeEventListener('resize', recalcPosition)
      window.removeEventListener('scroll', onScroll, true)
    })

    return {
      panelRef,
      panelStyle,
      resolveComponent,
      visibleItems,
    }
  },
}
</script>

<style scoped>
.main-menu-panel {
  position: fixed;
  background-color: #fff;
  border-radius: 0.358rem;
  box-shadow: 0 5px 25px rgba(0, 0, 0, 0.1);
  min-width: 200px;
  padding: 0.4rem 0;
  z-index: 11000;
  animation: menu-panel-enter 0.2s ease-out;
}

@keyframes menu-panel-enter {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.main-menu-panel__list {
  list-style: none;
  margin: 0;
  padding: 0;
}
</style>

<style>
.dark-layout .main-menu-panel {
  background-color: var(--light);
  box-shadow: 0 5px 25px rgba(0, 0, 0, 0.3);
}
</style>
